import React, {
	memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from '../../stores/types/types';
import { setPptxDeckAnimationsInfo } from '../../stores/actions/pptx-deck-animations';
import {
	PptxDeckPosition, PptxDeckSlideInfo, pptxDeckLinearIndex, pptxDeckReconcilePlan,
} from '../../utils/pptx-deck-animations-reconcile';

// Stable empty-array reference so the selector fallback doesn't create a new
// array identity on every render (which would re-fire the reconcile effect).
const EMPTY_SLIDES: PptxDeckSlideInfo[] = [];

// Max linear-step gap that is stepped (smoothly) rather than reloaded. Tunable.
const STEP_THRESHOLD = 5;
// Fallback timeout (ms) for a stuck step; kept high since early retries corrupt builds.
const STEP_TIMEOUT_MS = 20000;

interface Props {
	/** Base viewer URL of the converted pptx presentation (its `contentUrl`). */
	contentUrl: string;
	/** Media id — keys AllInfo in PptxDeckAnimationsReducer and (implicitly) this deck. */
	mediaId: number;
	/** 1-indexed current slide (synced source of truth). */
	currentPage: number;
	/** 1-indexed animation step within the slide (synced source of truth). */
	currentStep?: number;
	/** Called once the iframe document has loaded, to match the img/video loaded-count flow. */
	onLoad?: () => void;
	/**
	 * When a canvas drag is in progress, the iframe must stop capturing pointer
	 * events so native `dragover` reaches the parent `.media` drop area (iframes
	 * are separate browsing contexts and otherwise swallow the drag).
	 */
	globalIsDragging?: boolean;
	/**
	 * Called when the viewer reports a step change we never asked for (e.g. a
	 * PPTX build set to play automatically rather than on click). The caller
	 * should persist it as the new synced position so every client converges
	 * on it instead of the reconciler fighting to revert it.
	 */
	onAutoAdvance?: (pos: PptxDeckPosition) => void;
}

/**
 * Renders a pptx as an iframe and reconciles it
 * toward the synced (slide, step) target using the viewer's relative,
 * rate-limited NextStep/PreviousStep postMessages. Identical logic runs on every
 * client, so producer / presenter / broadcast stay in lockstep.
 */
export default memo(({
	contentUrl, mediaId, currentPage, currentStep, onLoad, globalIsDragging, onAutoAdvance,
}: Props) => {
	const dispatch = useDispatch();
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const slides = useSelector(
		(s: GreenroomState) => s.PptxDeckAnimationsReducer.info[mediaId]?.slides,
	) ?? EMPTY_SLIDES;

	// The slide the iframe is (re)loaded at. Only changes for reload-jumps.
	const [loadSlide, setLoadSlide] = useState(currentPage);

	// The stored contentUrl already carries a `#slideNumber=1` fragment; strip any
	// existing hash before applying the load slide so we don't stack fragments.
	const src = useMemo(() => {
		const base = contentUrl.split('#')[0];
		return `${base}#slideNumber=${loadSlide}`;
	}, [contentUrl, loadSlide]);

	// Where the iframe actually is, updated from echo messages.
	const appliedRef = useRef<PptxDeckPosition>({ slide: currentPage, step: currentStep ?? 1 });
	// Latest target/slides, mirrored into refs so event handlers avoid stale closures.
	const targetRef = useRef<PptxDeckPosition>({ slide: currentPage, step: currentStep ?? 1 });
	const slidesRef = useRef(slides);
	const inFlightRef = useRef(false);
	// Which kind of command is in flight, so only a stuck step (never a reload) gets preempted below.
	const inFlightKindRef = useRef<'step' | 'reload' | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	// An auto-advance we've locally accepted but haven't yet seen echoed back
	// through the `currentPage`/`currentStep` props (the sync round-trip). Until
	// then, keep using it as the target so we don't fight our own accepted
	// position against the stale prop value.
	const pendingAutoAdvanceRef = useRef<PptxDeckPosition | null>(null);
	const lastPropTargetRef = useRef<PptxDeckPosition>({
		slide: currentPage, step: currentStep ?? 1,
	});
	// The position + direction of the command currently in flight, so its echo
	// can be checked for overshoot (see onSettled).
	const sentFromRef = useRef<PptxDeckPosition | null>(null);
	const sentDirectionRef = useRef<1 | -1>(1);

	const propTarget = { slide: currentPage, step: currentStep ?? 1 };
	if (propTarget.slide !== lastPropTargetRef.current.slide
		|| propTarget.step !== lastPropTargetRef.current.step) {
		// The prop moved — either our own correction landed, or real new intent
		// (e.g. a Next click) arrived. Either way, trust the prop over any stale
		// pending override.
		pendingAutoAdvanceRef.current = null;
		lastPropTargetRef.current = propTarget;
	}
	targetRef.current = pendingAutoAdvanceRef.current ?? propTarget;
	slidesRef.current = slides;

	const post = useCallback((type: 'AllInfo' | 'NextStep' | 'PreviousStep') => {
		iframeRef.current?.contentWindow?.postMessage({ Type: type }, '*');
	}, []);

	// Drive one command toward the target; re-invoked on each echo/timeout.
	const step = useCallback(() => {
		if (inFlightRef.current) return;
		const plan = pptxDeckReconcilePlan(
			appliedRef.current,
			targetRef.current,
			slidesRef.current,
			STEP_THRESHOLD,
		);
		if (plan.type === 'noop') return;
		if (plan.type === 'reload') {
			inFlightRef.current = true; // released by the post-reload SlideComplete
			inFlightKindRef.current = 'reload';
			setLoadSlide(plan.slideNumber);
			return;
		}
		inFlightRef.current = true;
		inFlightKindRef.current = 'step';
		sentFromRef.current = appliedRef.current;
		sentDirectionRef.current = plan.commands[0] === 'NextStep' ? 1 : -1;
		post(plan.commands[0]);
		timeoutRef.current = setTimeout(() => {
			inFlightRef.current = false;
			step();
		}, STEP_TIMEOUT_MS);
	}, [post]);

	const onSettled = useCallback((pos: PptxDeckPosition) => {
		const wasInFlight = inFlightRef.current;
		const sentFrom = sentFromRef.current;
		const direction = sentDirectionRef.current;
		appliedRef.current = pos;
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		inFlightRef.current = false;
		inFlightKindRef.current = null;
		sentFromRef.current = null;

		// Fully unsolicited (no command was in flight at all), or our command
		// landed as asked but the deck advanced further on its own on top of it
		// (e.g. a build that auto-plays immediately on entering the new slide,
		// stacked onto the NextStep we sent to cross the slide boundary).
		let autoAdvanced = !wasInFlight;
		if (wasInFlight && sentFrom) {
			const expectedLinear = pptxDeckLinearIndex(slidesRef.current, sentFrom.slide, sentFrom.step)
				+ direction;
			const actualLinear = pptxDeckLinearIndex(slidesRef.current, pos.slide, pos.step);
			if ((actualLinear - expectedLinear) * direction > 0) autoAdvanced = true;
		}

		if (autoAdvanced) {
			// Accept it as the new baseline instead of reconciling back to the
			// stale target, and ask the caller to persist it so every client
			// converges on it via the normal sync channel.
			pendingAutoAdvanceRef.current = pos;
			targetRef.current = pos;
			onAutoAdvance?.(pos);
			return;
		}
		step();
	}, [step, onAutoAdvance]);

	// Listen for the iframe's messages (filtered to this iframe's window).
	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (e.source !== iframeRef.current?.contentWindow) return;
			const d = e.data;
			if (!d || !d.Type) return;
			switch (d.Type) {
				case 'SlideLoaded':
					post('AllInfo');
					break;
				case 'AllInfo':
					if (Array.isArray(d.Slides)) dispatch(setPptxDeckAnimationsInfo(mediaId, d.Slides));
					break;
				case 'SlideComplete':
				case 'SlideAnimationComplete':
					// The viewer reports CurrentStep 0-indexed (base state = 0,
					// range 0..TotalSteps inclusive); our (slide, step) model is
					// 1-indexed, so shift it up by one to keep applied/target comparable.
					onSettled({ slide: d.SlideIndex, step: d.CurrentStep + 1 });
					break;
				default:
					break;
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [mediaId, dispatch, post, onSettled]);

	// Reconcile whenever the target or the known deck structure changes.
	useEffect(() => {
		// A stuck step needing a reload is abandoned early; a reload itself is never preempted.
		if (inFlightRef.current && inFlightKindRef.current === 'step') {
			const wouldBe = pptxDeckReconcilePlan(
				appliedRef.current,
				targetRef.current,
				slidesRef.current,
				STEP_THRESHOLD,
			);
			if (wouldBe.type === 'reload') {
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				inFlightRef.current = false;
				inFlightKindRef.current = null;
				sentFromRef.current = null;
			}
		}
		step();
	}, [currentPage, currentStep, slides, step]);

	// Clear any pending step timeout on unmount so it never fires after teardown.
	useEffect(() => () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
	}, []);

	// A genuine document load lands the viewer at loadSlide, step 1 (the URL
	// fragment only sets the *initial* slide; runtime nav is postMessage-driven).
	// Correct the applied position from this ground truth and release any
	// in-flight reload, so a fresh load — initial mount OR a reload-jump —
	// re-seeks to the synced target, including reaching currentStep > 1 for
	// late joiners / spotlight re-parents.
	const handleLoad = useCallback(() => {
		appliedRef.current = { slide: loadSlide, step: 1 };
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		inFlightRef.current = false;
		inFlightKindRef.current = null;
		post('AllInfo');
		onLoad?.();
		step();
	}, [post, onLoad, loadSlide, step]);

	return (
		<iframe
			ref={iframeRef}
			className="media-iframe"
			src={src}
			title="presentation"
			onLoad={handleLoad}
			style={{ pointerEvents: globalIsDragging ? 'none' : undefined }}
			sandbox="allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	);
});
